/* eslint-disable react/forbid-foreign-prop-types */
import type { Requireable, Validator } from 'prop-types';
import type React from 'react';
import { convertPropType } from './describeConversions.js';
import descToTypescript from './toTypescript.js';
import type { ReactDescT, Hook, ComponentDocumentation, DocumentedReactComponent } from './types.js';

export default function describe<C, R>(ComponentInstance: React.ComponentType<C> | Hook<C, R>) {
  if (!ComponentInstance) {
    throw new Error('react-desc: component is required');
  }

  const documentation: ComponentDocumentation = {
    propTypes: {},
  };

  const DocumentedComponent = ComponentInstance as DocumentedReactComponent<C>;

  const addDocumentationProp = <T extends keyof ComponentDocumentation>(propName: T) => {
    const func = (value: ComponentDocumentation[T]): DocumentedReactComponent<C> => {
      documentation[propName] = value;
      return DocumentedComponent;
    };
    return func;
  };

  DocumentedComponent.availableAt = addDocumentationProp('availableAt');
  DocumentedComponent.description = addDocumentationProp('description');
  DocumentedComponent.details = addDocumentationProp('details');
  DocumentedComponent.deprecated = addDocumentationProp('deprecated');
  DocumentedComponent.usage = addDocumentationProp('usage');
  DocumentedComponent.intrinsicElement = addDocumentationProp('intrinsicElement');

  DocumentedComponent.toTypescript = () => descToTypescript(ComponentInstance, documentation);

  Object.defineProperty(DocumentedComponent, 'propTypes', {
    get: () => DocumentedComponent.propTypesValue,
    set: (value: Record<string, ReactDescT>) => {
      if (!DocumentedComponent.propTypesValue) {
        DocumentedComponent.propTypesValue = {};
      }

      Object.keys(value).forEach((name) => {
        const propType: ReactDescT = value[name];
        let realPropType: Requireable<unknown> | Validator<unknown>;
        documentation.propTypes[name] = propType;
        realPropType = convertPropType(propType);
        // We don't need to rely on the isRequired from prop-types package
        // So no need to invoke (realPropType).isRequired if our own proptype is required
        // Validation will handle that case for us
        DocumentedComponent.propTypesValue[name] = realPropType as Validator<unknown>;
        return propType;
      });
    },
    enumerable: true,
    configurable: true,
  });

  return DocumentedComponent;
}
